import { FC } from 'react';
import classNames from 'classnames';
import { SignatureUser } from '../../../generated/graphql';
import { getColorForSignatureUserType } from '../../lib/multiSignature';
import { formatSignatureDate } from '../../lib/signatureUtils';
import AnimatedStatusIndicator from './AnimatedStatusIndicator';

export interface SignatureUserData {
  id: string;
  orderIndex: number;
  signedAt?: string | null;
  signatureName?: string | null;
  signatureRole?: string | null;
  providerUser?: {
    name?: string | null;
  } | null;
  patientName?: string | null;
  assessmentSignatureUser?: {
    signatureUserType: SignatureUser;
  } | null;
}

interface VerticalSignatureStatusProps {
  signatureUsers: SignatureUserData[];
  className?: string;
  showStatus?: boolean;
  showNames?: boolean;
  currentSignerId?: string; // ID of the current signer who needs to sign
}

/**
 * Vertical variation of SignatureStatus component for sidebar layouts
 * Displays signers in a vertical list format with signature icon, colored name, and status
 */
const VerticalSignatureStatus: FC<VerticalSignatureStatusProps> = ({
  signatureUsers,
  className,
  showStatus = true,
  showNames = true,
  currentSignerId,
}) => {
  // Sort by order index
  const sortedUsers = [...signatureUsers].sort(
    (a, b) => a.orderIndex - b.orderIndex,
  );

  if (!signatureUsers || signatureUsers.length === 0) {
    return null;
  }

  return (
    <div className={classNames('space-y-1', className)}>
      {sortedUsers.map((signatureUser) => {
        const isSigned = Boolean(signatureUser.signedAt);
        const fullName =
          signatureUser.providerUser?.name ||
          signatureUser.patientName ||
          'Unknown';
        const signerType =
          signatureUser.assessmentSignatureUser?.signatureUserType ||
          SignatureUser.PatientUser;
        const isCurrentSigner = currentSignerId === signatureUser.id;
        const colorScheme = getColorForSignatureUserType(signerType);

        return (
          <div
            key={signatureUser.id}
            className="flex items-center gap-1.5"
            data-signer-id={signatureUser.id}
          >
            {/* Status indicator */}
            {showStatus && (
              <AnimatedStatusIndicator
                isSigned={isSigned}
                isCurrentSigner={isCurrentSigner}
                size="sm"
              />
            )}

            {/* Colored name badge */}
            {showNames && (
              <div
                className={classNames(
                  'text-neutral-700 inline-flex max-w-[180px] items-center rounded-full px-1.5 py-0.5 text-[11px] leading-[14px]',
                  colorScheme.backgroundColor,
                )}
              >
                <span className="truncate font-normal">{fullName}</span>
              </div>
            )}

            {/* Signature date for signed forms, or status for pending signers */}
            {isSigned ? (
              <span className="text-[11px] leading-[14px] text-neutral-110">
                {formatSignatureDate(signatureUser.signedAt)}
              </span>
            ) : isCurrentSigner ? (
              <span className="text-[11px] leading-[14px] text-neutral-110">
                Awaiting signature
              </span>
            ) : (
              <span className="text-[11px] leading-[14px] text-neutral-110">
                Not signed yet
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default VerticalSignatureStatus;
